import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api } from "@/lib/api";
import { log } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, ArrowUp, ArrowDown, Sparkle, X, DotsSixVertical, ClockCounterClockwise, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import TemplateLibraryDialog from "@/components/TemplateLibraryDialog";
import TemplatePreviewDialog from "@/components/TemplatePreviewDialog";
import AIRefineDialog from "@/components/AIRefineDialog";
import { Eye } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAccessGate } from "@/hooks/useAccessGate";

const TYPES = [
  { value: "single_choice", label: "Choix unique" },
  { value: "multiple_choice", label: "Choix multiple" },
  { value: "text", label: "Texte libre" },
  { value: "scale", label: "Échelle numérique" },
  { value: "likert", label: "Échelle Likert" },
  { value: "yes_no", label: "Oui / Non" },
  { value: "date", label: "Date" },
  { value: "number", label: "Nombre" },
];

const LIKERT_OPTS = [
  { label: "Pas du tout d'accord", value: 1 },
  { label: "Plutôt pas d'accord", value: 2 },
  { label: "Neutre", value: 3 },
  { label: "Plutôt d'accord", value: 4 },
  { label: "Tout à fait d'accord", value: 5 },
];

const newQuestion = (idx) => ({
  id: `q_${Date.now()}_${idx}`,
  type: "single_choice",
  label: "",
  instructions: "",
  required: true,
  options: [{ label: "", value: 0 }],
  min: 0, max: 10,
  score_weight: 0,
});

function SortableQuestion({ q, idx, total, updQ, move, rmQ, changeType, addOpt, updOpt, rmOpt }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-2xl p-6 transition-shadow ${isDragging ? "border-[#3A8FA5] shadow-[0_8px_24px_rgba(58,143,165,0.15)]" : "border-[#E1E9F0] hover:border-[#3A8FA5]/50"}`}
      data-testid={`q-${idx}`}
    >
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1.5 -ml-1.5 rounded-[8px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5] cursor-grab active:cursor-grabbing touch-none"
            aria-label="Réordonner par glisser-déposer"
            data-testid={`q-${idx}-drag`}
          >
            <DotsSixVertical size={18} weight="bold" />
          </button>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E6F1F5] border border-[#C7DEE6] text-[#3A8FA5] text-[11px] tracking-[0.12em] uppercase font-bold">
            Question {idx + 1}
          </span>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="rounded-[10px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5]" onClick={() => move(idx, -1)} disabled={idx === 0} title="Monter"><ArrowUp size={14} /></Button>
          <Button size="sm" variant="ghost" className="rounded-[10px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5]" onClick={() => move(idx, 1)} disabled={idx === total - 1} title="Descendre"><ArrowDown size={14} /></Button>
          <Button size="sm" variant="ghost" className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC]" onClick={() => rmQ(idx)} title="Supprimer"><Trash size={14} /></Button>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Consignes de la question <span className="normal-case tracking-normal text-[#5F758F]">(optionnel)</span></Label>
          <Textarea
            rows={2}
            placeholder="Ex : Pensez au dernier mois écoulé, cochez une seule réponse…"
            value={q.instructions || ""}
            onChange={(e) => updQ(idx, { instructions: e.target.value })}
            className="mt-1 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5] text-sm"
            data-testid={`q-${idx}-instructions`}
          />
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Question</Label>
          <Input placeholder="Intitulé de la question" value={q.label} onChange={(e) => updQ(idx, { label: e.target.value })}
            className="mt-1 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid={`q-${idx}-label`} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Select value={q.type} onValueChange={(v) => changeType(idx, v)}>
            <SelectTrigger className="bg-white border-[#E1E9F0] rounded-[10px]" data-testid={`q-${idx}-type`}><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <label
            htmlFor={`q-${idx}-required`}
            className="flex items-center justify-center gap-2 px-3 rounded-[10px] border border-[#E1E9F0] bg-white cursor-pointer select-none"
          >
            <Switch
              id={`q-${idx}-required`}
              checked={q.required}
              onCheckedChange={(v) => updQ(idx, { required: v })}
              className="data-[state=checked]:bg-[#3A8FA5] data-[state=unchecked]:bg-[#D1D5DB]"
              data-testid={`q-${idx}-required`}
            />
            <span className={`text-[11px] tracking-[0.12em] uppercase font-semibold ${q.required ? "text-[#3A8FA5]" : "text-[#5F758F]"}`}>
              {q.required ? "Obligatoire" : "Facultative"}
            </span>
          </label>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#5F758F]">Poids score</Label>
            <Input type="number" step="0.5" value={q.score_weight ?? 0}
              onChange={(e) => updQ(idx, { score_weight: parseFloat(e.target.value) || 0 })}
              className="w-20 bg-white border-[#E1E9F0] rounded-[10px]" />
          </div>
        </div>

        {["single_choice", "multiple_choice", "likert"].includes(q.type) && (
          <div className="space-y-2 pl-4 border-l-2 border-[#3A8FA5]/30">
            {(q.options || []).map((o, oi) => (
              <div key={o._uid || `${idx}-${oi}-${o.label}`} className="flex gap-2 items-center">
                <Input placeholder="Libellé" value={o.label} onChange={(e) => updOpt(idx, oi, { label: e.target.value })}
                  className="flex-1 bg-white border-[#E1E9F0] rounded-[10px]" />
                <Input type="number" placeholder="Val" value={o.value ?? ""} onChange={(e) => updOpt(idx, oi, { value: e.target.value === "" ? "" : Number(e.target.value) })}
                  className="w-20 bg-white border-[#E1E9F0] rounded-[10px]" />
                <Button size="sm" variant="ghost" className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC]" onClick={() => rmOpt(idx, oi)}><Trash size={14} /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-[10px] border-[#3A8FA5]/50 text-[#3A8FA5] hover:bg-[#E6F1F5] font-semibold" onClick={() => addOpt(idx)}>
              <Plus size={14} className="mr-1" /> Ajouter option
            </Button>
          </div>
        )}

        {q.type === "scale" && (
          <div className="grid grid-cols-4 gap-3 pl-4 border-l-2 border-[#3A8FA5]/30">
            <Input type="number" placeholder="Min" value={q.min} onChange={(e) => updQ(idx, { min: Number(e.target.value) })} className="bg-white border-[#E1E9F0] rounded-[10px]" />
            <Input type="number" placeholder="Max" value={q.max} onChange={(e) => updQ(idx, { max: Number(e.target.value) })} className="bg-white border-[#E1E9F0] rounded-[10px]" />
            <Input placeholder="Label min" value={q.min_label || ""} onChange={(e) => updQ(idx, { min_label: e.target.value })} className="bg-white border-[#E1E9F0] rounded-[10px]" />
            <Input placeholder="Label max" value={q.max_label || ""} onChange={(e) => updQ(idx, { max_label: e.target.value })} className="bg-white border-[#E1E9F0] rounded-[10px]" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function TemplateBuilder() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [tpl, setTpl] = useState({
    title: "", description: "", category: "", instructions: "",
    questions: [newQuestion(0)],
    scoring: { type: "none", interpretation: [], dimensions: [] },
    consent_required: true,
    consent_text: "",
  });
  const [busy, setBusy] = useState(false);
  const [validatedTpls, setValidatedTpls] = useState([]);
  const [seedId, setSeedId] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState(null); // { owner_id, owner_name, owner_cabinet_name } when editing a foreign template
  const { user } = useAuth();
  const { isTrialExpired, gateProps } = useAccessGate();
  const readOnly = !!(ownerInfo && ownerInfo.owner_id && user && ownerInfo.owner_id !== user.user_id);

  useEffect(() => {
    if (editing) return;
    (async () => {
      try {
        const { data } = await api.get("/templates");
        setValidatedTpls(data.filter((t) => t.is_validated));
      } catch (err) { log.warn("validated templates load failed", err); }
    })();
  }, [editing]);

  const seedFromValidated = (tplId) => {
    setSeedId(tplId);
    if (!tplId) {
      setTpl({
        title: "", description: "", category: "", instructions: "",
        questions: [newQuestion(0)],
        scoring: { type: "none", interpretation: [], dimensions: [] },
        consent_required: true,
        consent_text: "",
      });
      return;
    }
    const src = validatedTpls.find((t) => t.template_id === tplId);
    if (!src) return;
    setTpl({
      title: `${src.title} (copie)`,
      description: src.description || "",
      category: src.category || "",
      instructions: src.instructions || "",
      questions: src.questions.map((q, i) => ({
        ...q,
        id: `q_${Date.now()}_${i}`,
        options: q.options || [],
        score_weight: q.score_weight ?? 0,
      })),
      scoring: src.scoring
        ? { ...src.scoring, interpretation: src.scoring.interpretation || [], dimensions: src.scoring.dimensions || [] }
        : { type: "none", interpretation: [], dimensions: [] },
      consent_required: src.consent_required !== false,
      consent_text: src.consent_text || "",
    });
    toast.success(`Modèle "${src.title}" chargé — personnalisez-le à votre guise`);
  };

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/templates/${id}`);
        // Track owner metadata so we can flip the whole builder into a
        // read-only preview when the current user isn't the owner (shared
        // "Du service" templates — see Templates.jsx badge).
        setOwnerInfo({
          owner_id: data.owner_id,
          owner_name: data.owner_name || "",
          owner_cabinet_name: data.owner_cabinet_name || "",
        });
        setTpl({
          title: data.title, description: data.description || "", category: data.category || "",
          instructions: data.instructions || "",
          questions: data.questions.map((q) => ({ ...q, options: q.options || [], score_weight: q.score_weight ?? 0 })),
          scoring: data.scoring
            ? { ...data.scoring, interpretation: data.scoring.interpretation || [], dimensions: data.scoring.dimensions || [] }
            : { type: "none", interpretation: [], dimensions: [] },
          consent_required: data.consent_required !== false,
          consent_text: data.consent_text || "",
          visibility: data.visibility === "service" ? "service" : "private",
        });
      } catch { toast.error("Impossible de charger"); }
    })();
  }, [id, editing]);

  const updQ = (idx, patch) => setTpl((t) => ({ ...t, questions: t.questions.map((q, i) => i === idx ? { ...q, ...patch } : q) }));
  const move = (idx, dir) => setTpl((t) => {
    const arr = [...t.questions]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return t;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; return { ...t, questions: arr };
  });
  const reorder = (fromId, toId) => setTpl((t) => {
    const arr = [...t.questions];
    const from = arr.findIndex((q) => q.id === fromId);
    const to = arr.findIndex((q) => q.id === toId);
    if (from < 0 || to < 0 || from === to) return t;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    return { ...t, questions: arr };
  });
  const rmQ = (idx) => setTpl((t) => ({ ...t, questions: t.questions.filter((_, i) => i !== idx) }));
  const addQ = () => setTpl((t) => ({ ...t, questions: [...t.questions, newQuestion(t.questions.length)] }));

  const addOpt = (idx) => updQ(idx, { options: [...(tpl.questions[idx].options || []), { _uid: (crypto.randomUUID?.() || String(Date.now() + Math.random())), label: "", value: (tpl.questions[idx].options?.length || 0) }] });
  const updOpt = (qi, oi, patch) => updQ(qi, { options: tpl.questions[qi].options.map((o, i) => i === oi ? { ...o, ...patch } : o) });
  const rmOpt = (qi, oi) => updQ(qi, { options: tpl.questions[qi].options.filter((_, i) => i !== oi) });

  const changeType = (idx, type) => {
    const patch = { type };
    if (type === "likert") patch.options = LIKERT_OPTS.slice();
    else if (["single_choice", "multiple_choice"].includes(type)) patch.options = tpl.questions[idx].options?.length ? tpl.questions[idx].options : [{ label: "", value: 0 }];
    else patch.options = [];
    updQ(idx, patch);
  };

  const doSave = async () => {
    if (!tpl.title.trim()) return toast.error("Titre requis");
    if (tpl.questions.some((q) => !q.label.trim())) return toast.error("Chaque question doit avoir un intitulé");
    setBusy(true);
    try {
      const payload = { ...tpl, consent_required: true, scoring: tpl.scoring.type === "none" ? null : {
        ...tpl.scoring,
        // Strip empty/incomplete dimensions before persisting
        dimensions: (tpl.scoring.dimensions || [])
          .filter((d) => d.label && d.label.trim() && (d.question_ids || []).length > 0)
          .map((d) => ({
            key: d.key || d.label.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
            label: d.label.trim(),
            question_ids: d.question_ids,
            interpretation: (d.interpretation || []).filter((i) => i.label && i.label.trim()),
          })),
      } };
      const { data } = editing
        ? await api.put(`/templates/${id}`, payload)
        : await api.post("/templates", payload);
      toast.success(editing ? "Enregistré" : "Créé");
      nav(`/templates/${data.template_id}/edit`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const save = doSave;

  // Version history: fetched on demand when the user opens the panel.
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);
  const openHistory = async () => {
    setVersionsOpen(true);
    setVersionsLoading(true);
    try {
      const { data } = await api.get(`/templates/${id}/versions`);
      setVersions(data.versions || []);
    } catch { toast.error("Impossible de charger l'historique"); }
    finally { setVersionsLoading(false); }
  };
  const previewVersionSnap = async (v) => {
    try {
      const { data } = await api.get(`/templates/${id}/versions/${v.version_id}`);
      setPreviewVersion(data);
    } catch { toast.error("Version introuvable"); }
  };

  // --- AI refinement: replace current builder state with a Claude-refined draft.
  const [refineOpen, setRefineOpen] = useState(false);
  const applyAIRefine = (draft) => {
    setTpl({
      title: draft.title || "",
      description: draft.description || "",
      category: draft.category || "",
      instructions: draft.instructions || "",
      questions: (draft.questions || []).map((q, i) => ({
        ...q,
        // Rebuild stable client-side ids so drag-and-drop keys don't collide.
        id: `q_${Date.now()}_${i}`,
        options: (q.options || []).map((o) => ({ ...o })),
        score_weight: q.score_weight ?? 0,
      })),
      scoring: draft.scoring
        ? {
          ...draft.scoring,
          interpretation: draft.scoring.interpretation || [],
          dimensions: draft.scoring.dimensions || [],
        }
        : { type: "none", interpretation: [], dimensions: [] },
      consent_required: draft.consent_required !== false,
      consent_text: tpl.consent_text || "",
    });
  };

  // In read-only mode we cannot save. "Dupliquer" hits the
  // existing duplicate endpoint (server enforces read access via the
  // visibility filter) and navigates to the freshly-owned copy.
  const dupSharedTemplate = async () => {
    if (!id) return;
    try {
      const { data } = await api.post(`/templates/${id}/duplicate`);
      toast.success("Copie créée — vous en êtes désormais propriétaire");
      nav(`/templates/${data.template_id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplication impossible");
    }
  };

  return (
    <Protected role="admin">
      <AppShell
        title={editing ? "Modifier le questionnaire" : "Nouveau questionnaire"}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/templates")} className="rounded-[10px] font-semibold" data-testid="back-to-templates-btn">
              <ArrowLeft size={14} className="mr-1.5" />
              Retour aux questionnaires
            </Button>
            {editing && (
              <Button variant="outline" onClick={openHistory} className="rounded-[10px] font-semibold" data-testid="history-btn" title="Voir l'historique des versions">
                <ClockCounterClockwise size={14} className="mr-1.5" />
                Historique
              </Button>
            )}
            <Button
              variant="outline"
              disabled={!(tpl.questions || []).length || isTrialExpired}
              className="rounded-[10px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#E6F1F5] hover:text-[#1E3A5F] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ai-refine-btn"
              {...gateProps({
                onClick: () => setRefineOpen(true),
                title: "Affiner ce questionnaire avec l'IA",
              })}
            >
              <Sparkle size={14} weight="duotone" className="mr-1.5" />
              Régénérer avec l'IA
            </Button>
            <Button variant="outline" onClick={() => setPreviewOpen(true)} className="rounded-[10px] font-semibold" data-testid="preview-template-btn">
              <Eye size={14} weight="duotone" className="mr-1.5" />
              Prévisualiser
            </Button>
            {readOnly ? (
              <Button
                onClick={() => dupSharedTemplate()}
                className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold"
                data-testid="duplicate-shared-btn"
              >
                Dupliquer
              </Button>
            ) : (
              <Button
                className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="save-template-btn"
                {...gateProps({ onClick: save, disabled: busy, title: "Enregistrer" })}
              >
                {busy ? "…" : "Enregistrer"}
              </Button>
            )}
          </div>
        }
      >
        {readOnly && (
          <div className="mb-6 rounded-2xl bg-[#E6F1F5] border border-[#C7DEE6] px-5 py-4 flex items-start gap-3" data-testid="readonly-banner">
            <Eye size={18} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[14px] font-bold text-[#1E3A5F]">Lecture seule</p>
              <p className="text-[13px] text-[#5F758F] mt-0.5">
                Ce questionnaire est partagé par <strong className="text-[#1E3A5F]">{ownerInfo?.owner_name || "un autre praticien"}</strong>
                {ownerInfo?.owner_cabinet_name ? ` (${ownerInfo.owner_cabinet_name})` : ""}. Vous ne pouvez pas le modifier — cliquez sur « Dupliquer » pour en créer une copie personnelle.
              </p>
            </div>
          </div>
        )}
        <fieldset disabled={readOnly} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 ${readOnly ? "opacity-90" : ""}`}>
          <div className="lg:col-span-2 space-y-6">
            {!editing && validatedTpls.length > 0 && (
              <div className="bg-[#E6F1F5] border border-[#C7DEE6] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle size={16} weight="fill" className="text-[#3A8FA5]" />
                  <Label className="text-xs tracking-[0.2em] uppercase text-[#3A8FA5]">Partir d'un questionnaire validé</Label>
                </div>
                <p className="text-sm text-[#1E3A5F] mb-4">
                  Gagnez du temps en démarrant à partir d'un questionnaire reconnu. Vous pourrez ensuite tout modifier.
                </p>
                {seedId ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex-1 min-w-0 bg-white border border-[#3A8FA5] rounded-[10px] px-3 py-2 flex items-center gap-2">
                      <Sparkle size={14} weight="fill" className="text-[#3A8FA5] shrink-0" />
                      <span className="text-sm text-[#1E3A5F] truncate">
                        {validatedTpls.find((t) => t.template_id === seedId)?.title}
                      </span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setLibraryOpen(true)} className="rounded-[10px] font-semibold">
                      Changer
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => seedFromValidated("")} className="rounded-[10px] text-[#5F758F]" title="Repartir de zéro">
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => setLibraryOpen(true)}
                      className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold"
                      data-testid="open-library-btn"
                    >
                      Parcourir la bibliothèque
                    </Button>
                    <span className="text-sm text-[#5F758F] self-center">
                      ou continuez à partir d'un questionnaire vierge ci-dessous
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 space-y-4">
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Titre</Label>
                <Input value={tpl.title} onChange={(e) => setTpl({ ...tpl, title: e.target.value })}
                  className="mt-2 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="tpl-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Catégorie</Label>
                  <Input value={tpl.category} onChange={(e) => setTpl({ ...tpl, category: e.target.value })}
                    className="mt-2 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="tpl-category" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Description courte</Label>
                <Textarea rows={2} value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })}
                  className="mt-2 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="tpl-description"
                  placeholder="Ex : Suivi hebdomadaire de la douleur post-opératoire — 5 questions courtes." />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Consignes générales <span className="normal-case tracking-normal text-[#5F758F]">(optionnel)</span></Label>
                <Textarea rows={2} value={tpl.instructions} onChange={(e) => setTpl({ ...tpl, instructions: e.target.value })}
                  className="mt-2 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" placeholder="Ex : Répondez en pensant aux 2 dernières semaines…" />
                <p className="text-xs text-[#5F758F] mt-1.5">Affichées au patient sur l'écran d'accueil du questionnaire.</p>
              </div>
            </div>

            <div className="space-y-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => {
                  const { active, over } = e;
                  if (over && active.id !== over.id) reorder(active.id, over.id);
                }}
              >
                <SortableContext items={tpl.questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                  {tpl.questions.map((q, idx) => (
                    <SortableQuestion
                      key={q.id}
                      q={q}
                      idx={idx}
                      total={tpl.questions.length}
                      updQ={updQ}
                      move={move}
                      rmQ={rmQ}
                      changeType={changeType}
                      addOpt={addOpt}
                      updOpt={updOpt}
                      rmOpt={rmOpt}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <Button onClick={addQ} variant="outline" className="w-full rounded-2xl border-2 border-dashed border-[#3A8FA5]/50 text-[#3A8FA5] hover:bg-[#E6F1F5] hover:border-[#3A8FA5] font-semibold py-6" data-testid="add-question-btn">
                <Plus size={16} className="mr-2" /> Ajouter une question
              </Button>
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-4">
            {/* Visibility card — P3 Établissement. Placed above Scoring so the */}
            {/* practitioner sees this decision early (audience > mechanics). */}
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6" data-testid="tpl-visibility-card">
              <h3 className="text-xl font-bold tracking-tight text-[#1E3A5F] mb-3">Visibilité</h3>
              <div className="grid grid-cols-1 gap-2" data-testid="tpl-visibility-group">
                {[
                  { value: "private", title: "Privé", body: "Moi et mes assistants uniquement" },
                  { value: "service", title: "Partagé au service", body: "Visible et duplicable par les autres praticiens de mon service" },
                ].map((opt) => {
                  const active = (tpl.visibility ?? "private") === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setTpl({ ...tpl, visibility: opt.value })}
                      className={`text-left rounded-[10px] border px-4 py-3 transition-colors ${active ? "border-[#3A8FA5] bg-[#E6F1F5]" : "border-[#E1E9F0] bg-white hover:border-[#B7C4D6]"}`}
                      data-testid={`tpl-visibility-${opt.value}`}
                    >
                      <p className={`text-[13px] font-semibold ${active ? "text-[#3A8FA5]" : "text-[#1E3A5F]"}`}>{opt.title}</p>
                      <p className="text-[12px] text-[#5F758F] mt-0.5 leading-snug">{opt.body}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#5F758F] mt-3">Modifiable à tout moment. Vous restez le seul à pouvoir modifier ou supprimer ce questionnaire.</p>
            </div>
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-bold tracking-tight text-[#1E3A5F]">Scoring</h3>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Type</Label>
                <Select value={tpl.scoring.type} onValueChange={(v) => setTpl({ ...tpl, scoring: { ...tpl.scoring, type: v } })}>
                  <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    <SelectItem value="sum">Somme totale</SelectItem>
                    <SelectItem value="percentage">Pourcentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {tpl.scoring.type !== "none" && (
                <div>
                  <p className="text-xs text-[#5F758F] mb-2">Interprétations (min → max → étiquette)</p>
                  <div className="space-y-3">
                    {(tpl.scoring.interpretation || []).map((it, i) => (
                      <div key={it._uid || `${i}-${it.min}-${it.max}`} className="border border-[#E1E9F0] rounded-[10px] p-2.5 space-y-2 bg-[#F4F7FB]">
                        <div className="flex gap-2 items-center">
                          <Input type="number" placeholder="min" value={it.min} onChange={(e) => setTpl({ ...tpl, scoring: { ...tpl.scoring, interpretation: tpl.scoring.interpretation.map((x, j) => j === i ? { ...x, min: Number(e.target.value) } : x) } })} className="flex-1 bg-white border-[#E1E9F0]" />
                          <span className="text-[#5F758F] text-xs">→</span>
                          <Input type="number" placeholder="max" value={it.max} onChange={(e) => setTpl({ ...tpl, scoring: { ...tpl.scoring, interpretation: tpl.scoring.interpretation.map((x, j) => j === i ? { ...x, max: Number(e.target.value) } : x) } })} className="flex-1 bg-white border-[#E1E9F0]" />
                          <Button size="sm" variant="ghost" className="text-red-600 shrink-0 h-9 w-9 p-0" onClick={() => setTpl({ ...tpl, scoring: { ...tpl.scoring, interpretation: tpl.scoring.interpretation.filter((_, j) => j !== i) } })}><Trash size={14} /></Button>
                        </div>
                        <Input placeholder="Étiquette — ex : Anxiété modérée, nécessite un suivi clinique" value={it.label} onChange={(e) => setTpl({ ...tpl, scoring: { ...tpl.scoring, interpretation: tpl.scoring.interpretation.map((x, j) => j === i ? { ...x, label: e.target.value } : x) } })} className="w-full bg-white border-[#E1E9F0]" />
                      </div>
                    ))}
                    <Button size="sm" variant="outline" className="w-full rounded-[10px] font-semibold" onClick={() => setTpl({ ...tpl, scoring: { ...tpl.scoring, interpretation: [...(tpl.scoring.interpretation || []), { min: 0, max: 0, label: "" }] } })}>
                      <Plus size={14} className="mr-1" /> Ajouter palier
                    </Button>
                  </div>
                </div>
              )}

              {tpl.scoring.type !== "none" && (
                <div className="pt-4 border-t border-[#E1E9F0] space-y-3">
                  <div>
                    <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Sous-scores intermédiaires</p>
                    <p className="text-xs text-[#5F758F] mt-1">Regroupez des questions en dimensions (ex : « Effort », « Hyperactivité »). Chaque sous-score s'affichera séparément avec sa propre interprétation.</p>
                  </div>
                  <div className="space-y-3">
                    {(tpl.scoring.dimensions || []).map((d, di) => {
                      const updDim = (patch) => setTpl({ ...tpl, scoring: { ...tpl.scoring, dimensions: tpl.scoring.dimensions.map((x, j) => j === di ? { ...x, ...patch } : x) } });
                      const rmDim = () => setTpl({ ...tpl, scoring: { ...tpl.scoring, dimensions: tpl.scoring.dimensions.filter((_, j) => j !== di) } });
                      const toggleQ = (qid) => {
                        const cur = d.question_ids || [];
                        const next = cur.includes(qid) ? cur.filter((x) => x !== qid) : [...cur, qid];
                        updDim({ question_ids: next });
                      };
                      const addInterp = () => updDim({ interpretation: [...(d.interpretation || []), { min: 0, max: 0, label: "" }] });
                      const updInterp = (i, patch) => updDim({ interpretation: (d.interpretation || []).map((x, j) => j === i ? { ...x, ...patch } : x) });
                      const rmInterp = (i) => updDim({ interpretation: (d.interpretation || []).filter((_, j) => j !== i) });
                      const scorable = tpl.questions
                        .map((q, i) => ({ ...q, _number: i + 1 }))
                        .filter((q) => (q.score_weight ?? 0) !== 0);
                      return (
                        <div key={d._uid || `dim-${di}`} className="border border-[#E1E9F0] rounded-[14px] p-3 space-y-3 bg-[#F4F7FB]" data-testid={`dim-${di}`}>
                          <div className="flex gap-2 items-start">
                            <Input placeholder="Nom du sous-score (ex : Effort)" value={d.label || ""}
                              onChange={(e) => updDim({ label: e.target.value, key: (e.target.value || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || `dim_${di}` })}
                              className="bg-white border-[#E1E9F0]" data-testid={`dim-label-${di}`} />
                            <Button size="sm" variant="ghost" className="text-red-600 shrink-0" onClick={rmDim} data-testid={`dim-rm-${di}`}><Trash size={14} /></Button>
                          </div>
                          <div>
                            <p className="text-[11px] tracking-widest uppercase text-[#5F758F] mb-1.5">Questions incluses</p>
                            {scorable.length === 0 ? (
                              <p className="text-xs text-[#5F758F] italic">Aucune question avec un poids de score &gt; 0. Ajoutez un poids sur au moins une question.</p>
                            ) : (
                              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                                {scorable.map((q) => {
                                  const checked = (d.question_ids || []).includes(q.id);
                                  return (
                                    <label key={q.id} className={`flex items-start gap-2 text-xs cursor-pointer rounded px-1.5 py-1 -mx-1.5 transition-colors ${checked ? "bg-[#E1E9F0]/40" : "hover:bg-white"}`}>
                                      <Checkbox checked={checked} onCheckedChange={() => toggleQ(q.id)}
                                        className="mt-0.5 h-4 w-4 border-2 border-[#3A8FA5] data-[state=checked]:bg-[#3A8FA5] data-[state=checked]:border-[#3A8FA5] data-[state=checked]:text-white"
                                        data-testid={`dim-${di}-q-${q.id}`} />
                                      <span className={`shrink-0 tabular-nums font-medium ${checked ? "text-[#1E3A5F]" : "text-[#3A8FA5]"}`}>Q{q._number}.</span>
                                      <span className={`line-clamp-2 leading-tight ${checked ? "text-[#1E3A5F]" : "text-[#5F758F]"}`}>{q.label || <em className="text-[#5F758F]">(sans titre)</em>}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-[11px] tracking-widest uppercase text-[#5F758F] mb-1.5">Paliers d'interprétation (optionnel)</p>
                            <div className="space-y-2">
                              {(d.interpretation || []).map((it, i) => (
                                <div key={`dim-${di}-int-${i}`} className="border border-[#E1E9F0] rounded-[8px] p-2 space-y-1.5 bg-white">
                                  <div className="flex gap-1.5 items-center">
                                    <Input type="number" placeholder="min" value={it.min} onChange={(e) => updInterp(i, { min: Number(e.target.value) })} className="flex-1 bg-white border-[#E1E9F0] text-xs h-8" />
                                    <span className="text-[#5F758F] text-xs">→</span>
                                    <Input type="number" placeholder="max" value={it.max} onChange={(e) => updInterp(i, { max: Number(e.target.value) })} className="flex-1 bg-white border-[#E1E9F0] text-xs h-8" />
                                    <Button size="sm" variant="ghost" className="text-red-600 h-8 w-8 p-0 shrink-0" onClick={() => rmInterp(i)}><Trash size={12} /></Button>
                                  </div>
                                  <Input placeholder="Étiquette — ex : Score élevé" value={it.label} onChange={(e) => updInterp(i, { label: e.target.value })} className="w-full bg-white border-[#E1E9F0] text-xs h-8" />
                                </div>
                              ))}
                              <Button size="sm" variant="outline" className="w-full rounded-[10px] h-8 text-xs font-semibold" onClick={addInterp}>
                                <Plus size={12} className="mr-1" /> Ajouter palier
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <Button size="sm" variant="outline" className="w-full rounded-[10px] font-semibold"
                      onClick={() => setTpl({ ...tpl, scoring: { ...tpl.scoring, dimensions: [...(tpl.scoring.dimensions || []), { key: `dim_${(tpl.scoring.dimensions || []).length + 1}`, label: "", question_ids: [], interpretation: [], _uid: crypto.randomUUID?.() || String(Date.now()) }] } })}
                      data-testid="add-dimension-btn">
                      <Plus size={14} className="mr-1" /> Ajouter un sous-score
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </fieldset>
      </AppShell>
      <TemplateLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        templates={validatedTpls}
        onPick={seedFromValidated}
      />
      <TemplatePreviewDialog
        open={previewOpen}
        template={tpl}
        onClose={() => setPreviewOpen(false)}
      />
      <TemplatePreviewDialog
        open={!!previewVersion}
        template={previewVersion || tpl}
        onClose={() => setPreviewVersion(null)}
        headerLabel={previewVersion ? `Version #${previewVersion._version_number} · ${new Date(previewVersion._edited_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : undefined}
      />
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="versions-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">Historique des versions</DialogTitle>
            <DialogDescription className="text-[#5F758F]">
              Chaque enregistrement du questionnaire crée une nouvelle version. Vous pouvez consulter n'importe quelle formulation antérieure.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto -mx-6 px-6">
            {versionsLoading ? (
              <p className="text-center text-[#5F758F] py-8">Chargement…</p>
            ) : versions.length === 0 ? (
              <p className="text-center text-[#5F758F] py-8 italic">Aucune version antérieure — les enregistrements futurs apparaîtront ici.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {versions.map((v) => (
                  <div key={v.version_id} className="border border-[#E1E9F0] rounded-[12px] px-4 py-3 flex items-center gap-4 hover:border-[#3A8FA5] transition-colors" data-testid={`version-${v.version_number}`}>
                    <span className="w-10 h-10 rounded-[10px] bg-[#E6F1F5] text-[#3A8FA5] flex items-center justify-center font-bold text-[14px] tabular-nums shrink-0">#{v.version_number}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold text-[#1E3A5F] truncate">{v.title}</p>
                      <p className="text-[12px] text-[#5F758F] mt-0.5">
                        {new Date(v.edited_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {v.edited_by_name && <> · {v.edited_by_name}</>} · {v.question_count} question{v.question_count > 1 ? "s" : ""}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => previewVersionSnap(v)} className="rounded-[10px] font-semibold text-[13px]" data-testid={`preview-version-${v.version_number}`}>
                      <Eye size={14} className="mr-1.5" /> Prévisualiser
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AIRefineDialog
        open={refineOpen}
        onOpenChange={setRefineOpen}
        currentTpl={tpl}
        onApply={applyAIRefine}
      />
    </Protected>
  );
}
